'use client';

import React, { useState, useRef, useCallback } from 'react';
import { Send, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const MAX_MESSAGE_LENGTH = 2000;

export interface BotChatInputProps {
  onSendMessage: (content: string) => Promise<{ success: boolean; error?: string }>;
  disabled?: boolean;
  isSending?: boolean;
  placeholder?: string;
  className?: string;
}

export function BotChatInput({
  onSendMessage,
  disabled = false,
  isSending = false,
  placeholder = 'Type a message...',
  className,
}: BotChatInputProps) {
  const [message, setMessage] = useState('');
  const [localSending, setLocalSending] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const characterCount = message.length;
  const isOverLimit = characterCount > MAX_MESSAGE_LENGTH;
  const charactersRemaining = MAX_MESSAGE_LENGTH - characterCount;
  const showCharacterWarning = characterCount >= MAX_MESSAGE_LENGTH * 0.9;

  const sending = isSending || localSending;
  const canSend = !disabled && !sending && message.trim() && !isOverLimit;

  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 150)}px`;
    }
  }, []);

  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
    adjustTextareaHeight();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    if (!canSend) return;

    const trimmedMessage = message.trim();
    if (!trimmedMessage) return;

    setLocalSending(true);

    try {
      const result = await onSendMessage(trimmedMessage);

      if (result.success) {
        setMessage('');
        if (textareaRef.current) {
          textareaRef.current.style.height = 'auto';
        }
      }
    } finally {
      setLocalSending(false);
    }
  };

  return (
    <div className={cn('border-t border-[color:var(--jr-border)] bg-[color:rgba(22,13,13,0.92)] p-4 backdrop-blur-xl', className)} data-testid="bot-chat-input">
      <div className="mx-auto flex max-w-4xl items-start gap-2">
        <div className="flex-1 relative">
          <textarea
            ref={textareaRef}
            value={message}
            onChange={handleMessageChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled || sending}
            rows={1}
            className={cn(
              'jr-input min-h-[44px] max-h-[150px] w-full resize-none',
              'focus:ring-2 focus:ring-[color:var(--jr-accent-strong)] focus:ring-offset-0',
              'disabled:cursor-not-allowed disabled:opacity-50',
              isOverLimit && 'border-destructive focus:ring-destructive'
            )}
            data-testid="bot-chat-input-textarea"
          />

          {/* Character count indicator */}
          {showCharacterWarning && (
            <div
              className={cn(
                'absolute right-3 bottom-2 text-xs',
                isOverLimit ? 'font-medium text-[color:#efc2bd]' : 'text-[var(--jr-text-muted)]'
              )}
              data-testid="character-count"
            >
              {charactersRemaining}
            </div>
          )}
        </div>

        {/* Send button */}
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={!canSend}
          className="jr-action-primary h-11 w-11 shrink-0 self-start rounded-2xl px-0"
          aria-label="Send message"
          data-testid="bot-chat-send-button"
        >
          {sending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
        </Button>
      </div>
    </div>
  );
}
